/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { CellChange } from 'components/Spreadsheet/Spreadsheet.types';
import { IWorkbook } from 'models/interfaces/spreadsheet-types/workbook-types';
import GenerateCapTableJob from './GenerateCapTableJob';
import {
  ApiCreateDocumentGenerationJob,
  ApiService,
  CapTablesService,
  CompanyService,
  DocumentReferencesView,
  RetrieveDocumentGenerationJob,
  SecurityModel,
} from '../../../../../api';
import { SECURITY_NAME_ALIAS } from '../../../../../common/constants/cap-table/capTable';
import { useStore } from '../../../../../common/store';
import { UseStoreValues } from '../../../../../common/types/store';
import { HandlePushingAttributeMappingsPayload } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/attribute-mapped-widget-drawer.types';
import { AttributeMappedWidgetDrawer } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/AttributeMappedWidgetDrawer';
import { CitationPushedPayload } from '../../../../../components/Drawer/attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';
import WidgetDrawerProps from '../../../../../components/Drawer/widget-drawer/WidgetDrawer.props';
import { EventType, useEventBus, useEventSubscription } from '../../../../../context';
import { useAsyncProcessor } from '../../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { DocumentReference, useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import { useLayoutContext } from '../../../../../context/LayoutContext';
import { CellValue } from '../../../../../models/interfaces/spreadsheet-types/core-types';
import { useGetCaptableInfo, usePatchCapTableSecurities } from '../../../../../services/hooks/captable';
import { useGetDocumentsById } from '../../../../../services/hooks/documents';
import { CapTableReferenceGenerator, MappingReferenceData } from '../../../../../services/reference-generator';
import { CitationReferenceData } from '../../../../../services/reference-generator/types';
import { ClearAttributeMappingsPayload } from '../../../../../services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { useCellMappingStore, usePendingReviewCount, useReviewStore } from '../../../../../store';
import { useAttributeMapperStore } from '../../../../../store/useAttributeMapperStore';
import type { ReviewItem } from '../../../../../store/useReviewStore';
import { Logger, removeClassName } from '../../../../../utilities';
import { AttributeMappingType } from '../../../../Financials/financials-widget-drawer/types';
import { rowConfig } from '../../data';
import { aggregateReviewItems } from '../../utilities/reviewAggregator';
import { buildBulkItems, summarizeBulkResults } from '../../utilities/reviewBulk';
import { applyAcceptToSecurity, applyDismissToSecurity, declineRefsForFields } from '../../utilities/securityAiPatches';
import { useAIFieldAcceptContext } from '../captable-table/AIFieldAcceptContext';
import { CapTableCellWithReferences } from '../captable-table/CaptableTable.props';
import { useCitationContext } from '../captable-table/CitationContext';
import { ReviewSidePanel } from '../review-side-panel';
import { buildReviewDocJump, buildReviewExcelJump } from '../review-side-panel/reviewDocJump';
import ReviewDocumentsTabTitle from '../review-side-panel/ReviewDocumentsTabTitle';
import type { ReviewBulkResult } from '../review-side-panel/ReviewSidePanel.props';

export interface CapTableWidgetDrawerProps extends WidgetDrawerProps {
  workbook: IWorkbook;
  onTableChange: (cell: any, value: number) => void;
  captableSetProgrammaticUpdate?: (isProgrammatic: boolean) => void;
}

const CapTableWidgetDrawer = ({
  onTableChange,
  workbook,
  spreadsheetNotesProps,
  notesProps,
  documentReferencesProps,
  applicationContext,
  documentApplicationStorageKey,
  captableSetProgrammaticUpdate,
}: CapTableWidgetDrawerProps) => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { captableInfo, companyInfo } = storeValue;
  const { currentMeasurementDate } = useLayoutContext();
  const [, getCapTableInfo] = useGetCaptableInfo();
  const { getCellMapping } = useCellMappingStore();
  const { attributeMapperManager, getAttributeMapperManager, clearAttributeMapperManager } = useAttributeMapperStore();
  const { registerFootnote, clearFootnotes } = useDocumentFootnotes();
  const { addJob, removeJob } = useAsyncProcessor();
  const { acceptField, dismissField } = useAIFieldAcceptContext();
  const setReviewItems = useReviewStore(s => s.setReviewItems);
  // Pending-only count: reviewed items still live in the store (for the muted
  // Reviewed section), so the full length would keep the drawer auto-opening.
  const pendingReviewCount = usePendingReviewCount();
  const patchSecurities = usePatchCapTableSecurities();
  const { setActiveCitation } = useCitationContext();

  const { publish } = useEventBus();
  const [citation, setCitation] = useState<(CitationPushedPayload & { documentReferenceId: number }) | undefined>();
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [reprocessJobMetadata, setReprocessJobMetadata] = useState<{
    document_reference_id: number;
    measurement_date_id?: number | null;
    file_id?: number;
  } | null>(null);
  // Helper function to collect and register citation footnotes from cap table cells
  const registerCapTableCitationFootnotes = () => {
    if (!workbook?.captable?.cells) {
      return;
    }

    const cellsWithCitations = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).filter(
      cell => cell.citationReferences && cell.citationReferences.length > 0
    );

    if (cellsWithCitations.length === 0) {
      clearFootnotes();
      return;
    }

    // Group citations by cell to create unique footnote IDs
    cellsWithCitations.forEach(cell => {
      const cellKey = cell.key || `${cell.columnLegend}${cell.rowNumber}`;

      if (!cell.citationReferences) {
        return;
      }

      // Convert CitationReferenceData to DocumentReference format
      const documentReferencesMap = new Map<number, DocumentReference>();

      cell.citationReferences.forEach(citationRef => {
        const docId = citationRef.document_reference;

        if (!documentReferencesMap.has(docId)) {
          documentReferencesMap.set(docId, {
            documentId: docId,
            fields: [],
          });
        }

        const docRef = documentReferencesMap.get(docId)!;

        // For citations, we use the field_name and create pseudo cell references from token positions
        const existingFieldIndex = docRef.fields.findIndex(f => f.fieldName === citationRef.field_name);

        const cellReferences = citationRef.tokenReferences.map(tokenRef => ({
          ref: `p${tokenRef.page}-${tokenRef.token_ids.join(',')}`,
          direction: 'positive' as const,
        }));

        if (existingFieldIndex >= 0) {
          // Merge cell references into existing field
          docRef.fields[existingFieldIndex].cellReferences.push(...cellReferences);
        } else {
          // Add new field entry
          docRef.fields.push({
            fieldName: citationRef.field_name,
            sheetName: 'Citation', // Citations don't have sheet names, use a placeholder
            cellReferences,
          });
        }
      });

      // Register footnote for this cell
      registerFootnote(`captable-cell-${cellKey}`, Array.from(documentReferencesMap.values()));
    });
  };

  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell]: [{ cellInfo: CapTableCellWithReferences }]) => {
      if (citation) {
        return;
      }
      const refs = newCell.cellInfo.mappingReferences;
      if (refs && refs.length > 0) {
        const first = refs[0];
        publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, {
          documentReferenceId: first.document_reference,
          sheetName: first.sheet_name,
        });
      }
    }
  );

  // this might move down into the md viewer
  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell, _prevCell]: [{ cellInfo: CapTableCellWithReferences }, CapTableCellWithReferences]) => {
      if (!citation) {
        return;
      }

      const existingRefIndex = newCell.cellInfo.citationReferences?.findIndex(
        item => item.field_name === newCell.cellInfo.alias && item.document_reference === citation.documentReferenceId
      );

      if (existingRefIndex !== undefined && existingRefIndex !== -1 && newCell.cellInfo.citationReferences) {
        // Add to existing citation's token references
        newCell.cellInfo.citationReferences[existingRefIndex].tokenReferences.push({
          token_ids: citation.tokenIds,
          page: citation.page,
          plain_text: citation.selectedText,
          notes: [],
        });
        newCell.cellInfo.citationReferences[existingRefIndex].is_accepted = true;
      } else {
        const createdCitation: CitationReferenceData = {
          document_reference: citation.documentReferenceId,
          field_name: newCell.cellInfo.alias ?? '',
          is_accepted: true,
          tokenReferences: [
            {
              token_ids: citation.tokenIds,
              page: citation.page,
              plain_text: citation.selectedText,
              notes: [],
            },
          ],
        };

        newCell.cellInfo.citationReferences = newCell.cellInfo.citationReferences
          ? [...newCell.cellInfo.citationReferences, createdCitation]
          : [createdCitation];
      }

      onTableChange(newCell.cellInfo, newCell.cellInfo.value as number);
      setCitation(undefined);

      enqueueSnackbar(`Citation added to ${newCell.cellInfo.alias}(${newCell.cellInfo.key})`, { variant: 'success' });

      registerCapTableCitationFootnotes();
    }
  );

  // Update footnotes whenever citations in cells change
  useEffect(() => {
    registerCapTableCitationFootnotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbook?.captable?.cells]);

  // Cleanup footnotes on unmount
  useEffect(
    () => () => {
      clearFootnotes();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // SCAL-8976: every document referenced by a cell's citations/mappings. These
  // back the per-card document name; resolved via the same documents-by-id query
  // the doc-view header uses, so names are reliable rather than best-effort.
  const referencedDocumentIds = useMemo(() => {
    const cells = workbook?.captable?.cells as Record<string, CapTableCellWithReferences> | undefined;
    if (!cells) {
      return [] as string[];
    }
    const ids = new Set<string>();
    Object.values(cells).forEach(cell => {
      [...(cell.citationReferences ?? []), ...(cell.mappingReferences ?? [])].forEach(ref => {
        if (ref.document_reference != null) {
          ids.add(String(ref.document_reference));
        }
      });
    });
    return Array.from(ids);
  }, [workbook?.captable?.cells]);

  // Resolve the cards' document names, but DEFER this batch until the browser is idle.
  // It fetches every referenced document in parallel; firing it on drawer open made those
  // N requests compete (browser caps ~6 connections/host) with the critical document-content
  // and tab queries, which left the preview/tabs intermittently blank until a window resize.
  // Deferring lets the document the user opens load first; the names fill in a beat later.
  const [docNamesEnabled, setDocNamesEnabled] = useState(false);
  useEffect(() => {
    const idleWindow = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    if (idleWindow.requestIdleCallback) {
      const handle = idleWindow.requestIdleCallback(() => setDocNamesEnabled(true), { timeout: 2500 });
      return () => idleWindow.cancelIdleCallback?.(handle);
    }
    const handle = window.setTimeout(() => setDocNamesEnabled(true), 1200);
    return () => window.clearTimeout(handle);
  }, []);

  const { data: referencedDocuments } = useGetDocumentsById({
    documentIds: referencedDocumentIds,
    enabled: docNamesEnabled,
  });

  const documentNamesByRefId = useMemo(() => {
    const map = new Map<number, string>();
    Object.values(referencedDocuments ?? {}).forEach(doc => {
      if (doc.file?.filename) {
        map.set(doc.document_reference_id, doc.file.filename);
      }
    });
    return map;
  }, [referencedDocuments]);

  // Derive the "Pending Review" cards from the parsed cells and push them into the
  // review store. Re-runs when the resolved document names arrive so the cards'
  // documentNames fill in instead of rendering empty on first paint.
  useEffect(() => {
    const cells = workbook?.captable?.cells as Record<string, CapTableCellWithReferences> | undefined;
    setReviewItems(cells ? aggregateReviewItems(cells, documentNamesByRefId) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbook?.captable?.cells, documentNamesByRefId, setReviewItems]);

  // Clear review items on unmount so a different page's drawer never inherits them.
  useEffect(
    () => () => {
      setReviewItems([]);
    },
    [setReviewItems]
  );

  // SCAL-8976 (fix #1): when the cap table loads with pending review items, open
  // the bottom drawer ONCE so the Pending Review panel is visible. Guarded by a
  // ref so it never re-opens after the user closes it (expandDrawer is itself a
  // no-op when the drawer is already open, so a user-resized drawer is untouched).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current || pendingReviewCount === 0) {
      return;
    }
    autoOpenedRef.current = true;
    publish(EventType.EXPAND_DRAWER, undefined);
  }, [pendingReviewCount, publish]);

  // SCAL-8976 perf: reflect a single accept/decline on the grid INCREMENTALLY. The
  // old path replaced the whole securities array (patchSecurities), which forced a
  // full ~5-6s grid re-parse and froze the UI after the card moved. Instead we update
  // only the affected cell - and, via the normal edit path, its formula dependents -
  // and keep the AI metadata flagged (accepted/dismissed) so the review aggregator
  // still derives the card into the Reviewed section. The securities store is left
  // untouched on purpose: the cells are the source of truth for the panel and the Save
  // reverse-parse, the backend already persisted the change, and the next fetch
  // reconciles. The AI-proposed security NAME needs column-level handling, so it keeps
  // the existing (full) path.
  const findAiCell = useCallback(
    (securityId: number, fieldAlias: string) =>
      Object.values((workbook?.captable?.cells ?? {}) as Record<string, CapTableCellWithReferences>).find(
        c => Number(c.columnId) === securityId && c.alias === fieldAlias && c.aiFieldMetadata != null
      ),
    [workbook]
  );

  const acceptFieldOnGrid = useCallback(
    (securityId: number, fieldAlias: string) => {
      const cell = findAiCell(securityId, fieldAlias);
      if (!cell?.aiFieldMetadata) {
        return;
      }
      const aiValue = cell.aiFieldMetadata.value;
      cell.aiFieldMetadata = { ...cell.aiFieldMetadata, status: 'accepted', is_accepted: true };
      if (aiValue != null && String(aiValue) !== String(cell.value)) {
        // Apply the accepted value through the edit path so dependents (ownership %,
        // fully-diluted) recalc, but as a PROGRAMMATIC update so handleCellChanged does
        // NOT strip the metadata we just flagged. The same programmatic flag preserves the
        // cell's Excel mapping reference (a manual edit clears it), so color-by-reference
        // keeps the cell GREEN iff a mapping reference survives - accept never forces green
        // for a citation-only finding (SCAL-9152). The merge text auto-hides because the
        // value now equals the AI value. try/finally so the flag never stays stuck.
        captableSetProgrammaticUpdate?.(true);
        try {
          onTableChange(cell, aiValue as number);
        } finally {
          captableSetProgrammaticUpdate?.(false);
        }
      } else {
        cell.dispatchEvent?.();
      }
    },
    [findAiCell, onTableChange, captableSetProgrammaticUpdate]
  );

  const dismissFieldOnGrid = useCallback(
    (securityId: number, fieldAlias: string) => {
      const cell = findAiCell(securityId, fieldAlias);
      if (!cell?.aiFieldMetadata) {
        return;
      }
      // Soft decline mirror of applyDismissToSecurity (SCAL-8976/9152): flag the metadata
      // dismissed and UNLINK the field's PDF citation through the shared declineRefsForFields
      // path (single source - no duplicated ref logic). The Excel mapping reference is
      // deliberately LEFT intact so color-by-reference keeps a prior-reference cell GREEN,
      // while a citation-only cell has no mapping reference and reverts to non-green. The
      // value is not touched: a pending field never applied the AI value, so the settled
      // AIMergeValueViewer renders cell.value (no arrow) - the revert to the prior value is
      // automatic.
      cell.aiFieldMetadata = { ...cell.aiFieldMetadata, status: 'dismissed', is_accepted: false };
      cell.citationReferences = declineRefsForFields(cell.citationReferences, [fieldAlias]);
      cell.dispatchEvent?.();
    },
    [findAiCell]
  );

  // The optional reviewer note from the card is forwarded as `reason` (persisted as
  // review_reason). The panel flips the card's status optimistically once these
  // resolve, MOVING it to the muted Reviewed section.
  const handleReviewAccept = useCallback(
    async (securityId: number, fieldAlias: string, reason?: string) => {
      if (fieldAlias === SECURITY_NAME_ALIAS) {
        await acceptField(securityId, fieldAlias, reason);
        return;
      }
      try {
        await CapTablesService.apiCapTablesSecuritiesAcceptAiFieldsPartialUpdate(String(securityId), {
          field_names: [fieldAlias],
          ...(reason ? { reason } : {}),
        });
      } catch (error) {
        enqueueSnackbar('Failed to accept the suggestion', { variant: 'error' });
        throw error;
      }
      acceptFieldOnGrid(securityId, fieldAlias);
    },
    [acceptField, acceptFieldOnGrid]
  );

  const handleReviewDecline = useCallback(
    async (securityId: number, fieldAlias: string, reason?: string) => {
      if (fieldAlias === SECURITY_NAME_ALIAS) {
        await dismissField(securityId, fieldAlias, reason);
        return;
      }
      try {
        await CapTablesService.apiCapTablesSecuritiesDismissAiFieldsPartialUpdate(String(securityId), {
          field_names: [fieldAlias],
          ...(reason ? { reason } : {}),
        });
      } catch (error) {
        enqueueSnackbar('Failed to decline the suggestion', { variant: 'error' });
        throw error;
      }
      dismissFieldOnGrid(securityId, fieldAlias);
    },
    [dismissField, dismissFieldOnGrid]
  );

  // Bulk accept/decline the selected pending cards in ONE request, then reflect each
  // settled field on the grid incrementally (no full rebuild). Any settled NAME field
  // still needs the in-place security patch (column-level styling), so those are
  // collected and applied in a single patch - one rebuild only when names are present.
  const handleReviewBulkSettle = useCallback(
    async (action: 'accept' | 'decline', items: ReviewItem[], reason?: string): Promise<ReviewBulkResult> => {
      const capTableId = captableInfo?.id;
      const batchItems = buildBulkItems(new Set(items.map(i => i.id)), items);
      if (capTableId == null || batchItems.length === 0) {
        return { settledIds: [] };
      }

      try {
        const { results } = await CapTablesService.apiCapTablesAiReviewBatchPartialUpdate(String(capTableId), {
          action,
          items: batchItems,
          ...(reason ? { reason } : {}),
        });

        // Fold the partial-success response: settled fields per security, the moved
        // card ids, and any per-item errors (see summarizeBulkResults).
        const { settledBySecurity, settledIds, errorMessages } = summarizeBulkResults(results);

        const applyOnGrid = action === 'accept' ? acceptFieldOnGrid : dismissFieldOnGrid;
        let hasNameSettlement = false;
        settledBySecurity.forEach((fields, securityId) => {
          fields.forEach(fieldAlias => {
            if (fieldAlias === SECURITY_NAME_ALIAS) {
              hasNameSettlement = true;
            } else {
              applyOnGrid(securityId, fieldAlias);
            }
          });
        });

        // NAME columns keep the in-place security patch (the only case that rebuilds).
        if (hasNameSettlement) {
          const applyToSecurity = action === 'accept' ? applyAcceptToSecurity : applyDismissToSecurity;
          patchSecurities(securities =>
            securities.map(s => {
              const nameFields = (s.id != null ? settledBySecurity.get(s.id) : undefined)?.filter(
                f => f === SECURITY_NAME_ALIAS
              );
              return nameFields?.length ? (applyToSecurity(s, nameFields) as SecurityModel) : s;
            })
          );
        }

        if (errorMessages.length > 0) {
          enqueueSnackbar(
            `${errorMessages.length} item(s) could not be ${action === 'accept' ? 'accepted' : 'declined'}.`,
            { variant: 'warning' }
          );
          Logger.warn('CapTableWidgetDrawer - bulk review partial failure', errorMessages);
        }

        return { settledIds };
      } catch (error) {
        Logger.error('CapTableWidgetDrawer - bulk review failed', error);
        enqueueSnackbar(`Failed to ${action} the selected items`, { variant: 'error' });
        return { settledIds: [] };
      }
    },
    [captableInfo?.id, acceptFieldOnGrid, dismissFieldOnGrid, patchSecurities]
  );

  // SCAL-8976 (Feature B): the card's eyeball icon reveals its PRIMARY source
  // citation in the PDF viewer, highlighted by the citation's REVIEW STATUS
  // (pending -> AI-purple outline, reviewed -> plain yellow; SCAL-8986). Mirrors
  // CitationValueViewer.onView (setActiveCitation + NAVIGATE_TO_DOCUMENT_REFERENCE).
  // NO-OP for items with no document-backed citation.
  const handleReviewRevealPdf = useCallback(
    (item: ReviewItem) => {
      const jump = buildReviewDocJump(item);
      if (!jump) {
        return;
      }
      setActiveCitation(jump.activeCitation, jump.variant);
      publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, jump.navigatePayload);
    },
    [setActiveCitation, publish]
  );

  // SCAL-8976 (Feature C): a review card body becoming active reveals its PRIMARY
  // Excel mapping-reference source - open the xlsx viewer to the mapped sheet and
  // highlight the mapped cell(s) via NAVIGATE_TO_DOCUMENT_REFERENCE. Unlike the
  // eyeball PDF jump it does NOT setActiveCitation. NO-OP for items with no Excel
  // mapping reference (buildReviewExcelJump returns null).
  const handleReviewRevealExcel = useCallback(
    (item: ReviewItem) => {
      const jump = buildReviewExcelJump(item);
      if (!jump) {
        return;
      }
      publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, jump.navigatePayload);
    },
    [publish]
  );

  const clearCellReferences = (docRefPayload?: ClearAttributeMappingsPayload) => {
    const cellList = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).filter(item => {
      const hasMappedRefs = item.mappingReferences?.length;
      const hasColumnarRefs = item.columnarReferences?.length;
      const hasColRefsMetadata = item.columnarReferencesMetadata?.length;
      const hasCitationRefs = item.citationReferences?.length;

      return hasMappedRefs || hasColumnarRefs || hasColRefsMetadata || hasCitationRefs;
    });

    const cellUpdates: { cell: CapTableCellWithReferences; value: CellValue }[] = [];
    cellList.forEach(cell => {
      // If no docRefPayload, clear all references
      if (!docRefPayload) {
        cell.mappingReferences = [];
        cell.columnarReferences = [];
        cell.columnarReferencesMetadata = [];
        cell.className = removeClassName(cell.className ?? '', 'cell-with-references');
        cell.valueViewer = null;

        cellUpdates.push({
          cell,
          value: cell.value,
        });
        return;
      }

      // Otherwise, filter references for specific document/sheet
      const { documentReferenceId, sheet } = docRefPayload;
      let hasChanges = false;

      // Filter mappingReferences
      if (cell.mappingReferences && cell.mappingReferences.length > 0) {
        const originalLength = cell.mappingReferences.length;
        cell.mappingReferences = cell.mappingReferences.filter(ref => {
          const matchesDoc = ref.document_reference === documentReferenceId;
          const matchesSheet = !sheet || ref.sheet_name === sheet;
          return !(matchesDoc && matchesSheet);
        });
        hasChanges = hasChanges || originalLength !== cell.mappingReferences.length;
      }

      const hasAnyReferences = cell.mappingReferences && cell.mappingReferences.length > 0;

      if (hasChanges && !hasAnyReferences) {
        cell.mappingReferences = [];
        cell.columnarReferences = [];
        cell.columnarReferencesMetadata = [];
        cell.className = removeClassName(cell.className ?? '', 'cell-with-references');
        cell.valueViewer = null;
      }

      if (hasChanges) {
        cellUpdates.push({
          cell,
          value: cell.value,
        });
      }
    });

    cellUpdates.forEach(change => {
      onTableChange(change.cell, change.value as number);
    });

    // Recalculate title cells for affected columns
    if (docRefPayload && cellUpdates.length > 0) {
      const referenceGeneratorService = new CapTableReferenceGenerator();
      const affectedColumns = new Set<string>();

      cellUpdates.forEach(({ cell }) => {
        const { columnLegend } = cell as CapTableCellWithReferences;
        if (columnLegend) {
          affectedColumns.add(columnLegend);
        }
      });

      affectedColumns.forEach(columnLegend => {
        const capCell = workbook.captable.cells[`${columnLegend}1`] as CapTableCellWithReferences;
        if (capCell) {
          const columnMappingReferences: MappingReferenceData[] = [];
          Object.keys(workbook.captable.cells).forEach(key => {
            if (key.startsWith(columnLegend) && key !== `${columnLegend}1`) {
              const cell = workbook.captable.cells[key] as CapTableCellWithReferences;
              if (cell.mappingReferences && cell.mappingReferences.length > 0) {
                columnMappingReferences.push(...cell.mappingReferences);
              }
            }
          });
          const docRefsData = referenceGeneratorService.getDocumentReferences(columnMappingReferences);
          capCell.columnarReferences = docRefsData.documentIds;
          capCell.columnarReferencesMetadata = docRefsData.metadata;
          capCell.dispatchEvent();
        }
      });
    }
  };

  const captableIndex = (alias: string) =>
    rowConfig({
      isDisabled: false,
      measurementDate: '',
      customTitles: {},
      firmName: '',
    }).findIndex(item => item.alias === alias);

  const pushAttributeMappingsToCapTable = async (payload?: HandlePushingAttributeMappingsPayload) => {
    const mapper = getAttributeMapperManager();
    if (!mapper) {
      return;
    }

    // Set flag to indicate programmatic updates are happening
    captableSetProgrammaticUpdate?.(true);

    try {
      const securityFields = rowConfig({ isDisabled: false, measurementDate: '', customTitles: {} }).map(
        item => item.alias
      );
      const mappingService = new CapTableReferenceGenerator(securityFields);

      // find which cell is in an expr state
      const cellsInExprState = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).filter(
        cell => cell.expr === '='
      );

      if (!cellsInExprState.length) {
        return;
      }

      if (!payload?.documentReferencePayload) {
        return;
      }

      cellsInExprState.forEach(cell => {
        const titleCellForColumn = workbook.captable.cells[`${cell.columnLegend}1`] as CapTableCellWithReferences;

        // need to clear similar xValue and yValue pairs from the mapper
        const expectedXValue = cell.alias;
        const expectedYValue = titleCellForColumn.value;

        // for cap table each single cell selected in the table must only be represented by one value below
        mapper.removeSingleCellByXYValue({
          xValue: expectedXValue,
          yValue: expectedYValue,
        });

        mapper.handleSingularAddition(payload.documentReferencePayload!, {
          // coordinates should be the cell in the drawer sheet
          coordinates: `${payload.selectedCell?.cellInfo.key}`,

          // these are the source values
          xValue: cell.alias,
          yValue: titleCellForColumn.value,
          direction: 'positive',
        });
      });

      const mapping = getCellMapping();
      const evaluatedAttributeData = mapper.calculateValues(mapping);

      // Get the sheet attribute mapping which contains the reference data
      const { sheetAttributeMapping } = mapper;

      // Generate mapping references from the sheet attribute data (not evaluated data)
      const generatedReferences = mappingService.generateMappingReferences(sheetAttributeMapping.data);

      const captableChangeSet: CellChange[] = [];

      // clearCellReferences();

      Object.keys(evaluatedAttributeData).forEach(columnarMetric => {
        Object.keys(evaluatedAttributeData[columnarMetric]).forEach(rowMetric => {
          const captableRowIndex = captableIndex(rowMetric);

          if (captableRowIndex >= 0) {
            const updatedCell = Object.values(workbook.captable.cells as CapTableCellWithReferences[]).find(
              (item: CapTableCellWithReferences) => {
                // Only consider cells that are in mapping mode (expr === '=')
                if (item.expr !== '=') {
                  return false;
                }

                const titleColumn = workbook.captable.cells[`${item.columnLegend}1`];
                if (!titleColumn) {
                  return false;
                }

                return item.alias === rowMetric && titleColumn.value === columnarMetric;
              }
            ) as CapTableCellWithReferences | undefined;
            if (updatedCell) {
              const mappedValue = evaluatedAttributeData[columnarMetric][rowMetric].value;

              // // Attach reference metadata to the cell
              const referenceMetadata = mappingService.getReferenceMetadataForCell(
                columnarMetric,
                rowMetric,
                generatedReferences
              );

              // // Add mapping reference metadata to cell
              const titleCell = workbook.captable.cells[`${updatedCell.columnLegend}1`];

              if (referenceMetadata) {
                updatedCell.mappingReferences = referenceMetadata;
                const columnarRefs = mappingService.getDocumentReferences(referenceMetadata);

                // set col refs on column
                titleCell.columnarReferences = columnarRefs.documentIds;
                titleCell.columnarReferencesMetadata = columnarRefs.metadata;

                captableChangeSet.push({
                  cell: titleCell,
                  value: titleCell.value,
                });
              } else {
                updatedCell.mappingReferences = [];
                titleCell.columnarReferences = [];
                titleCell.columnarReferencesMetadata = [];
              }

              // here add in the cell refs for the col

              captableChangeSet.push({
                cell: updatedCell,
                value: mappedValue,
              });
            }
          }
        });
      });

      const fullChanges = captableChangeSet;
      fullChanges.forEach(change => {
        onTableChange(change.cell, change.value);
      });

      if (payload?.showNotification) {
        enqueueSnackbar('Mapping pushed to captable', { variant: 'success' });
      }
    } finally {
      // Reset flag after all changes are complete
      captableSetProgrammaticUpdate?.(false);
    }
  };

  const handleClearMappings = (docRefPayload?: ClearAttributeMappingsPayload) => {
    clearCellReferences(docRefPayload);
    clearAttributeMapperManager(docRefPayload);
    pushAttributeMappingsToCapTable(undefined);

    registerCapTableCitationFootnotes();
  };

  const filterRows = row => !row.readOnly;
  const mapRowsToOptions = row => ({ label: row.value, value: row.alias });

  const attributeReferenceConfig = useMemo(() => {
    if (!captableInfo) {
      return { columns: [], rows: [], selectionMode: AttributeMappingType.SINGLE_CELL };
    }

    const columns = captableInfo.securities?.map((security: SecurityModel) => ({
      label: security.name,
      value: security.id,
    }));

    const captableRows = rowConfig({
      isDisabled: false,
      measurementDate: '',
      customTitles: {},
      firmName: '',
    });
    const captableOptions = captableRows.filter(filterRows).map(mapRowsToOptions);
    const rows = [{ label: 'CapTable', value: null, isTitle: true }, ...captableOptions];
    return {
      columns,
      rows,
      selectionMode: AttributeMappingType.SINGLE_CELL,
    };
  }, [captableInfo]);

  const handleCitationPushed = (payload: CitationPushedPayload, documentReferenceId: number) => {
    setCitation({ ...payload, documentReferenceId });
  };

  const handleReprocessClick = useCallback(
    (documentClicked: DocumentReferencesView) => {
      setReprocessJobMetadata({
        document_reference_id: documentClicked.document_reference_id,
        measurement_date_id: currentMeasurementDate?.id,
        file_id: documentClicked.file.id,
      });
      setReprocessDialogOpen(true);
    },
    [currentMeasurementDate?.id]
  );

  const handleReprocessCancel = () => {
    setReprocessJobMetadata(null);
    setReprocessDialogOpen(false);
  };

  const handleReprocessConfirm = async () => {
    setReprocessDialogOpen(false);
    if (!reprocessJobMetadata || reprocessJobMetadata.file_id == null) {
      enqueueSnackbar('No document information available to reprocess', { variant: 'error' });
      return;
    }
    if (!companyInfo?.id) {
      enqueueSnackbar('Company information not available', { variant: 'error' });
      return;
    }

    const companyIdStr = companyInfo.id.toString();
    const fileId = reprocessJobMetadata.file_id;

    try {
      const newJob = await ApiService.apiCompanyDocumentGenerationJobsNewCreate(companyIdStr, {
        processing_type: ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
        reference_files: [fileId.toString()] as any,
        desired_termination_state: 'COMPLETE',
        parser_overrides: [
          {
            file_type: 'xlsx',
            xlsx_parsing_mode: 'JSON_CONVERSION',
            minify_json: true,
          } as any,
        ],
        metadata: {
          document_reference_id: reprocessJobMetadata.document_reference_id,
          measurement_date_id: reprocessJobMetadata.measurement_date_id,
          file_id: fileId,
        },
      } as any);

      Logger.info('CapTableWidgetDrawer:', `Adding cap table job ${newJob.job_id} to polling`);

      addJob({
        id: newJob.job_id,
        details: {
          title: 'Reprocessing Cap Table',
          description: `Reprocessing document ${reprocessJobMetadata.document_reference_id}`,
        },
        pollingConfig: {
          pollingFunction: async () =>
            CompanyService.apiCompanyDocumentGenerationJobsRead(companyIdStr, newJob.job_id),
          pollingInterval: 3000,
          pollingArgs: [],
        },
        onResult: async (result: RetrieveDocumentGenerationJob) => {
          if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
            try {
              const applyResult = (await ApiService.apiCompanyCapTablesApplyGenerationJobCreate(
                companyIdStr,
                { job_id: result.job_id }
              )) as unknown as { security_ids?: number[] };

              // Cast: hook return tuple is typed loosely; see useGetCaptableInfo.
              await (getCapTableInfo as (id?: number) => Promise<void>)(captableInfo?.id);

              const count = applyResult?.security_ids?.length ?? 0;
              enqueueSnackbar(
                count > 0
                  ? `Cap table updated with ${count} securities from AI results`
                  : 'Cap table reprocessing completed',
                { variant: 'success' }
              );
            } catch (applyError) {
              Logger.error('CapTableWidgetDrawer - Error applying reprocessed job', applyError);
              enqueueSnackbar('Job completed but applying results to the cap table failed', {
                variant: 'warning',
              });
            }
            removeJob(result.job_id);
          } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
            enqueueSnackbar('Reprocessing job failed', { variant: 'error' });
            removeJob(result.job_id);
          }
        },
        onError: (error: Error) => {
          Logger.error('CapTableWidgetDrawer - Reprocess job polling error', error);
        },
      });

      enqueueSnackbar('Reprocessing started. The cap table will update when the job completes.', {
        variant: 'info',
      });
    } catch (error: any) {
      Logger.error('CapTableWidgetDrawer - Error reprocessing document', error);
      const errorMessage = error?.body?.detail || error?.message || 'Failed to reprocess document';
      enqueueSnackbar(`Error: ${errorMessage}`, { variant: 'error' });
    } finally {
      setReprocessJobMetadata(null);
    }
  };

  return (
    <>
      <AttributeMappedWidgetDrawer
        documentReferenceHeaderContent={<GenerateCapTableJob />}
        documentApplicationStorageKey={documentApplicationStorageKey}
        attributeMapper={attributeMapperManager}
        attributeReferenceConfig={attributeReferenceConfig}
        onCitationPushed={handleCitationPushed}
        onReprocessDocument={handleReprocessClick}
        applicationContext={applicationContext}
        documentReferencesProps={documentReferencesProps}
        notesProps={notesProps}
        spreadsheetNotesProps={spreadsheetNotesProps}
        handlePushingAttributeMappings={pushAttributeMappingsToCapTable}
        handleClearMappings={handleClearMappings}
        sidePanel={
          <ReviewSidePanel
            onAccept={handleReviewAccept}
            onDecline={handleReviewDecline}
            onBulkSettle={handleReviewBulkSettle}
            onRevealPdf={handleReviewRevealPdf}
            onRevealExcel={handleReviewRevealExcel}
            documentNamesByRefId={documentNamesByRefId}
          />
        }
        documentsTabTitle={<ReviewDocumentsTabTitle />}
      />

      <Dialog open={reprocessDialogOpen} onClose={handleReprocessCancel}>
        <DialogTitle>Reprocess Cap Table Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will run a new AI cap table generation job on this document. When it completes,
            the AI results will be applied to the cap table and existing securities will be replaced.
            <br />
            <br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReprocessCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => handleReprocessConfirm()} color="primary" variant="contained">
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CapTableWidgetDrawer;
